import { computed, ref } from 'vue'

import { pickSignificantFilters } from './filtersQuery'
import type { ListFilter } from './types'
import { useFilters } from './useFilters'

/**
 * The filter panel of a list page: the URL-backed values, the open/closed state of the
 * panel, and the apply/reset handlers the controlled filter form emits into.
 *
 * The URL stays the single source of truth — this only wraps `useFilters`. The panel
 * starts open when the page was opened with filters already in the URL (a link from
 * another table), because a narrowed list with no visible reason why is confusing.
 *
 * `toListFilter` turns the panel's values into what the endpoint is actually sent. The
 * default drops the empty entries; pass your own only when the endpoint words a filter
 * differently from the panel — a spot travelling as `sourceable_id` +
 * `sourceable_type`, say.
 */
export function useFilterPanel<
  TValues extends Record<string, unknown>,
  TFilter extends ListFilter = TValues
>(
  scope: string,
  initial: TValues,
  toListFilter: (
    filters: Record<string, unknown>
  ) => TFilter = pickSignificantFilters as (
    filters: Record<string, unknown>
  ) => TFilter
) {
  const { currentFilters, enabled, updateFilters, clearFilters } = useFilters(
    scope,
    initial as Record<string, unknown>
  )

  const isOpen = ref(enabled.value)

  function toggle() {
    isOpen.value = !isOpen.value
  }

  return {
    /** Values for the filter form — always contains every key, mostly nulls. */
    filterValues: computed(() => currentFilters.value as TValues),
    currentFilters,
    listFilter: computed(() => toListFilter(currentFilters.value)),
    isFilterPanelOpen: isOpen,
    toggleFilterPanel: toggle,

    onFilterApply: (next: TValues) =>
      updateFilters(next as Record<string, unknown>),
    onFilterReset: clearFilters
  }
}
